import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { networkManager } from '../api/NetworkManager';
import { getCurrentUser, refreshAccessToken } from '../api';
import type { UserProfile, AuthTokens } from '../api';

// Role → capabilities mapping (mirrors backend permissions.ts)
const ROLE_CAPABILITIES: Record<string, string[]> = {
  super_admin: ['*'],
  admin: ['event:create','event:edit','event:view','event:transition','user:manage','user:assign-roles','team:view','submission:view-all','score:view-all','leaderboard:view','leaderboard:manage','live:control','live:view','bulk:invite','bulk:export','bulk:transition','bulk:assign','ai:insights','admin'],
  organizer: ['event:edit','event:view','event:transition','team:view','submission:view-all','score:view-all','leaderboard:view','leaderboard:manage','live:control','live:view','bulk:invite','bulk:export','bulk:assign'],
  participant: ['event:view','team:create','team:join','team:view','submission:create','submission:edit','submission:view-own','leaderboard:view','live:view','ai:refine','ai:generate'],
  judge: ['event:view','team:view','submission:view-all','score:submit','score:view-own','leaderboard:view','live:view','ai:summarize'],
  mentor: ['event:view','team:view','submission:view-all','feedback:give','feedback:view','leaderboard:view','live:view','ai:refine','ai:summarize'],
};

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Apply a fresh set of tokens + profile after login or registration. */
  applyAuth: (tokens: AuthTokens, profile: UserProfile) => void;
  logout: () => void;
  can: (capability: string) => boolean;
  hasRole: (role: string) => boolean;
  refreshProfile: () => Promise<void>;
  // Role emulation (super_admin only)
  emulatedRole: string | null;
  emulateRole: (role: string | null) => void;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const ACCESS_TOKEN_KEY = 'byte_access_token';
const REFRESH_TOKEN_KEY = 'byte_refresh_token';

function readStoredTokens(): { accessToken: string | null; refreshToken: string | null } {
  try {
    return {
      accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
      refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
    };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

function clearStoredTokens() {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Legacy Cognito keys — remove if present
    localStorage.removeItem('byte_id_token');
  } catch {
    // ignore storage errors
  }
}

function writeStoredTokens(accessToken: string, refreshToken?: string) {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch {
    // ignore storage errors
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emulatedRole, setEmulatedRole] = useState<string | null>(null);

  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const logout = useCallback(() => {
    clearStoredTokens();
    networkManager.setToken(null);
    setUser(null);
    setEmulatedRole(null);
  }, []);

  const applyAuth = useCallback((tokens: AuthTokens, profile: UserProfile) => {
    writeStoredTokens(tokens.accessToken, tokens.refreshToken);
    networkManager.setToken(tokens.accessToken);
    setUser(profile);
  }, []);

  const can = useCallback(
    (capability: string) => {
      if (!user) return false;
      if (emulatedRole) {
        const caps = ROLE_CAPABILITIES[emulatedRole] ?? [];
        return caps.includes('*') || caps.includes(capability);
      }
      return user.capabilities.includes(capability);
    },
    [user, emulatedRole],
  );

  const hasRole = useCallback(
    (role: string) => {
      if (!user) return false;
      if (emulatedRole) return emulatedRole === role;
      return user.roles.includes(role);
    },
    [user, emulatedRole],
  );

  const emulateRole = useCallback(
    (role: string | null) => {
      if (!isSuperAdmin) return;
      setEmulatedRole(role);
    },
    [isSuperAdmin],
  );

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await getCurrentUser();
      setUser(profile);
    } catch {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    const { accessToken, refreshToken } = readStoredTokens();

    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    networkManager.setToken(accessToken);

    getCurrentUser()
      .then((profile) => setUser(profile))
      .catch(async () => {
        // Access token expired or invalid — try refresh once
        if (!refreshToken) {
          logout();
          return;
        }
        try {
          const refreshed = await refreshAccessToken(refreshToken);
          writeStoredTokens(refreshed.accessToken);
          networkManager.setToken(refreshed.accessToken);
          const profile = await getCurrentUser();
          setUser(profile);
        } catch {
          logout();
        }
      })
      .finally(() => setIsLoading(false));
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        applyAuth,
        logout,
        can,
        hasRole,
        refreshProfile,
        emulatedRole,
        emulateRole,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
